import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Card, Spin, Typography } from 'antd'
import { IPageConfig } from '@/Core/Contracts/Loaders/IPageConfig'
import { Form, FormField } from '@/Core/UI'
import { Button } from '@/Core/UI/Button'
import { createFormContext } from '@/Core/UI/Form/Providers'
import { FiEdit2 } from 'react-icons/fi'
import Tabs from '@/Core/UI/Tabs'
import { UnderDevelopment } from '@/Ship/Components/UI/UnderDevelopment'
import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { UserResource } from '../Resources/UserResource'
import { UserProfileEditTemplate } from '../Templates/UserProfileEditTemplate'
import { UserProfileStore } from '../Stores/UserProfileStore'
import { ChangePasswordTab } from '../Components/ChangePasswordTab'

const { Title } = Typography

export const pageConfig: IPageConfig = {
    title: 'Профіль користувача',
}

/** Read-only "Основна інформація" tab. */
const GeneralInfoTab: React.FC<{ userData: any; onEdit: () => void }> = ({ userData, onEdit }) => {
    const formContext = createFormContext({
        fields: UserResource.fields,
        initialValues: userData,
        isEditable: false,
    })

    return (
        <Card style={{ marginBottom: 16 }}>
            <Form formContext={formContext}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Title level={5} style={{ margin: 0 }}>Загальна інформація</Title>
                    {!formContext.isEditable && (
                        <Button type="link" icon={<FiEdit2 />} onClick={onEdit}>
                            Редагувати
                        </Button>
                    )}
                </div>
                <FormField name="fullName" />
                <FormField name="phone" />
                <FormField name="email" />
                <FormField name="position" />
                <FormField name="dateOfBirth" />
                <FormField name="status" />
                <FormField name="createdAt" />
                <FormField name="updatedAt" />
            </Form>
        </Card>
    )
}

const UserProfilePage: React.FC = observer(() => {
    const [store] = useState(() => new UserProfileStore())

    useEffect(() => {
        store.load()
    }, [store])

    if (store.loading) {
        return (
            <div style={{ padding: '24px', display: 'flex', justifyContent: 'center' }}>
                <Spin size="large" />
            </div>
        )
    }

    const userData = store.userData

    const handleEdit = () => {
        // Inline-clone of UserResource with custom edit template — kept as-is per Artem.
        const customResource = {
            ...UserResource,
            key: 'Users',
            detailConfig: {
                ...UserResource.detailConfig,
                modeConfig: {
                    ...UserResource.detailConfig?.modeConfig,
                    edit: {
                        ...UserResource.detailConfig?.modeConfig?.edit,
                        template: UserProfileEditTemplate,
                    },
                },
            },
        }
        OpenResourceCardTask({
            resource: customResource,
            primaryFieldValue: userData?.uuid || userData?.id,
            mode: 'edit',
            displayMode: 'drawer',
            onClose: () => store.reload(),
        })
    }

    const tabItems = [
        {
            label: 'Особисті дані',
            key: 'general',
            children: <GeneralInfoTab userData={userData} onEdit={handleEdit} />,
        },
        {
            label: 'Змінити пароль',
            key: 'password',
            children: <ChangePasswordTab userId={userData?.uuid || userData?.id} />,
        },
        {
            label: 'Налаштування',
            key: 'settings',
            children: (
                <UnderDevelopment
                    title="Налаштування"
                    description="Тут ви зможете налаштувати параметри профілю користувача, права доступу та персональні налаштування"
                />
            ),
        },
    ]

    return <Tabs items={tabItems} />
})

export default UserProfilePage
